<template>
  <div
    ref="container"
    class="cf-phone-number-input"
    :class="{
      'cf-phone-number-input--has-content': !!nationalDigits,
      'cf-phone-number-input--has-error-message': !hideError && errorMessage,
      'cf-phone-number-input--picker-open': isPickerOpen
    }"
    :style="{ '--country-button-width': countryButtonWidth ? `${countryButtonWidth}px` : null }"
  >
    <button
      ref="countryButton"
      type="button"
      class="cf-phone-number-input__country-button"
      :disabled="disabled"
      :aria-haspopup="true"
      :aria-expanded="isPickerOpen"
      :aria-label="`Country code: +${selectedCountryData.callingCode}. Press to change country.`"
      @click="togglePicker"
    >
      <span
        class="cf-phone-number-input__flag"
        aria-hidden="true"
      >{{ selectedCountryData.flag }}</span>

      <span
        class="cf-phone-number-input__chevron cf-icon fas fa-chevron-down"
        aria-hidden="true"
      />

      <span class="cf-phone-number-input__calling-code">+{{ selectedCountryData.callingCode }}</span>
    </button>

    <input
      :id="id || name"
      ref="input"
      class="cf-phone-number-input__input"
      type="tel"
      :value="nationalDisplay"
      :name="name"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :disabled="disabled"
      inputmode="tel"
      @input="inputHandler"
      @focus="focusHandler"
      @blur="blurHandler"
      @keydown="keydownHandler"
    >

    <label
      ref="label"
      class="cf-phone-number-input__label"
      :for="id || name"
    >
      {{ label }}

      <span
        v-if="showAsOptional && !allValidations.required"
        class="gray regular smaller"
      >
        (Optional)
      </span>
    </label>

    <div
      v-if="isPickerOpen"
      class="cf-phone-number-input__picker"
      role="dialog"
      :aria-label="`Select country`"
    >
      <input
        ref="searchInput"
        v-model="searchTerm"
        type="search"
        class="cf-phone-number-input__picker-search"
        placeholder="Search countries"
        @keydown.esc.prevent="closePicker"
      >

      <ul class="cf-phone-number-input__picker-list">
        <li
          v-for="country in filteredCountries"
          :key="country.iso"
          :class="[
            'cf-phone-number-input__picker-item',
            { 'cf-phone-number-input__picker-item--selected': country.iso === selectedCountryIso }
          ]"
          role="button"
          tabindex="0"
          @click="selectCountry(country.iso)"
          @keydown.enter.prevent="selectCountry(country.iso)"
          @keydown.space.prevent="selectCountry(country.iso)"
        >
          <span
            class="cf-phone-number-input__picker-flag"
            aria-hidden="true"
          >{{ country.flag }}</span>

          <span class="cf-phone-number-input__picker-name">{{ country.name }}</span>

          <span class="cf-phone-number-input__picker-code">+{{ country.callingCode }}</span>
        </li>

        <li
          v-if="filteredCountries.length === 0"
          class="cf-phone-number-input__picker-empty"
        >
          No countries match "{{ searchTerm }}"
        </li>
      </ul>
    </div>

    <cf-input-error
      v-if="!hideError"
      :error="errorMessage"
    />
  </div>
</template>



<script>
  import { inject, computed, watch } from 'vue';
  import { useField } from 'vee-validate';
  import { AsYouType, parsePhoneNumberWithError, getCountries, getCountryCallingCode } from 'libphonenumber-js';

  import getPhoneCountries from '@/helpers/get-phone-countries';
  import detectDefaultPhoneCountry from '@/helpers/detect-default-phone-country';

  import CfInputError from '@/view/components/shared/cf-input-error/index.vue';



  const COUNTRIES = getPhoneCountries();
  const COUNTRIES_BY_ISO = COUNTRIES.reduce((map, country) => {
    map[country.iso] = country;
    return map;
  }, {});


  /*
    Extract the ISO country code and national digits from a v-model value
    (canonical E.164 with a leading '+'). Returns null when the value can't
    be parsed at all — callers fall back to defaults.

    Two paths:
      1. libphonenumber's parsePhoneNumber. When it pins both a country and
         a national number, that's the best answer.
      2. A manual calling-code lookup. libphonenumber sometimes parses a
         number but leaves `country` undefined (e.g. ambiguous NANP area
         codes, or rule-of-thumb validation rejecting unusual central-office
         codes). Even then we can still split the calling-code prefix off
         the digits and look up the first country that shares that code.
         Country fidelity is lower in this fallback (e.g. CA vs US for +1)
         but we'd rather show the user their saved number than render empty.
  */
  function parseE164(value) {
    if (!value) return null;

    try {
      const parsed = parsePhoneNumberWithError(String(value));

      if (parsed && parsed.country && parsed.nationalNumber) {
        return {
          iso: parsed.country,
          digits: parsed.nationalNumber
        };
      }

      if (parsed && parsed.nationalNumber) {
        return {
          iso: null,
          digits: parsed.nationalNumber
        };
      }
    }
    catch (_error) { /* fall through */ }

    const digitsOnly = String(value).replace(/^\+/, '');

    if (!/^\d+$/.test(digitsOnly)) return null;

    /*
      Calling codes are 1–3 digits. Try the longest prefix first so we
      don't mis-attribute a +44 number to +4 (which is unused).
    */
    for (let prefixLength = 3; prefixLength >= 1; prefixLength--) {
      const prefix = digitsOnly.slice(0, prefixLength);
      const nationalDigits = digitsOnly.slice(prefixLength);

      if (nationalDigits) {
        const matchingCountry = getCountries().find(iso => getCountryCallingCode(iso) === prefix);

        if (matchingCountry) {
          return {
            iso: matchingCountry,
            digits: nationalDigits
          };
        }
      }
    }

    return null;
  }



  export default {
    name: 'CfPhoneNumberInput',

    components: {
      CfInputError
    },

    props: {
      autocomplete: {
        type: String,
        default: 'tel'
      },

      defaultCountry: {
        type: String,
        default: null
      },

      disabled: {
        type: Boolean,
        default: false
      },

      hideError: {
        type: Boolean,
        default: false
      },

      id: {
        type: String,
        default: null
      },

      label: {
        type: String,
        required: true
      },

      name: {
        type: String,
        required: true
      },

      placeholder: {
        type: String,
        default: null
      },

      showAsOptional: {
        type: Boolean,
        default: false
      },

      validations: {
        type: Object,
        default() {
          return {};
        }
      },

      modelValue: {
        type: String,
        default: null
      }
    },

    /*
      VeeValidate 4 requires the Composition API to register a field with the
      parent form context. setup() is used exclusively for that purpose — all
      other component logic remains in the Options API below.
    */
    setup(props) {
      const formFieldset = inject('formFieldset', {});

      const rules = computed(() => {
        if (formFieldset.isDisabled) return {};

        return { phone_number: true, ...props.validations };
      });

      const { errorMessage, handleChange } = useField(() => props.name, rules, {
        initialValue: props.modelValue,
        label: props.label
      });

      watch(() => props.modelValue, handleChange);

      return { errorMessage, allValidations: rules };
    },

    data() {
      const initial = parseE164(this.modelValue);

      return {
        selectedCountryIso: (initial && initial.iso) || this.defaultCountry || detectDefaultPhoneCountry(),
        nationalDigits: (initial && initial.digits) || '',
        isPickerOpen: false,
        searchTerm: '',
        countryButtonWidth: 0
      };
    },

    computed: {
      selectedCountryData() {
        return COUNTRIES_BY_ISO[this.selectedCountryIso] || COUNTRIES_BY_ISO.US;
      },

      nationalDisplay() {
        if (!this.nationalDigits) return '';

        return new AsYouType(this.selectedCountryIso).input(this.nationalDigits);
      },

      e164Value() {
        if (!this.nationalDigits) return null;

        const callingCode = getCountryCallingCode(this.selectedCountryIso);

        return `+${callingCode}${this.nationalDigits}`;
      },

      filteredCountries() {
        const term = this.searchTerm.trim().toLowerCase();

        if (!term) return COUNTRIES;

        return COUNTRIES.filter((country) => {
          if (country.name.toLowerCase().includes(term)) return true;
          if (country.iso.toLowerCase().includes(term)) return true;
          if (`+${country.callingCode}`.includes(term)) return true;
          return false;
        });
      }
    },

    watch: {
      modelValue(value) {
        this.syncFromModelValue(value);
      },

      selectedCountryIso(newIso) {
        /*
          The dial-code label width changes when the country changes (e.g.
          '+1' → '+995'), so we re-measure on the next paint.
        */
        this.$nextTick(this.measureCountryButton);

        /*
          Re-evaluate the stored digits against the new country's rules. If
          the user had typed digits that included the old country's calling
          code, the leading-digit stripping in `cleanDigitsForCountry` will
          re-apply for the new country here too.
        */
        this.nationalDigits = this.cleanDigitsForCountry(this.nationalDigits, newIso);
        this.emitModelValue();
      }
    },

    mounted() {
      this.measureCountryButton();
    },

    beforeUnmount() {
      document.removeEventListener('mousedown', this.documentClickHandler);
    },

    methods: {
      blurHandler(event) {
        this.$emit('blur', event);
      },

      closePicker() {
        if (!this.isPickerOpen) return;

        this.isPickerOpen = false;
        this.searchTerm = '';
        document.removeEventListener('mousedown', this.documentClickHandler);
      },

      documentClickHandler(event) {
        if (!this.$refs.container) return;
        if (this.$refs.container.contains(event.target)) return;

        this.closePicker();
      },

      emitModelValue() {
        this.$emit('update:modelValue', this.e164Value);
      },

      focus() {
        this.$refs.input.focus();
      },

      focusHandler(event) {
        this.$emit('focus', event);
      },

      inputHandler(event) {
        this.nationalDigits = this.cleanDigitsForCountry(
          event.target.value.replace(/\D/g, ''),
          this.selectedCountryIso
        );
        this.emitModelValue();
      },

      cleanDigitsForCountry(digits, iso) {
        const callingCode = getCountryCallingCode(iso);

        let cleaned = digits;

        /*
          Strip a leading country-calling-code from the typed digits. NANP
          (US/CA) area codes never start with 1, so a leading '1' in a US
          input is always the country-code prefix; the same is true for
          most other dial codes. Without this, the leading-1 is preserved
          in storage and visually duplicated in the button's '+1' label
          and AsYouType's formatted output.
        */
        if (cleaned.startsWith(callingCode)) {
          cleaned = cleaned.slice(callingCode.length);
        }

        /*
          Cap at the E.164 max of 15 digits total minus the country code.
          Generous for every country — most have national-number maximums
          well below this — but prevents pathological unbounded input.
        */
        const maxNationalLength = 15 - callingCode.length;
        if (cleaned.length > maxNationalLength) {
          cleaned = cleaned.slice(0, maxNationalLength);
        }

        return cleaned;
      },

      measureCountryButton() {
        const button = this.$refs.countryButton;
        if (!button) return;

        this.countryButtonWidth = button.offsetWidth;
      },

      keydownHandler(event) {
        this.$emit('keydown', event);
      },

      openPicker() {
        if (this.isPickerOpen || this.disabled) return;

        this.isPickerOpen = true;
        document.addEventListener('mousedown', this.documentClickHandler);

        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      },

      selectCountry(iso) {
        this.selectedCountryIso = iso;
        this.emitModelValue();
        this.closePicker();

        this.$nextTick(() => {
          this.$refs.input.focus();
        });
      },

      syncFromModelValue(value) {
        if (value === this.e164Value) return;

        const parsed = parseE164(value);

        if (parsed) {
          if (parsed.iso) this.selectedCountryIso = parsed.iso;
          this.nationalDigits = parsed.digits;
        }

        else if (value == null || value === '') {
          this.nationalDigits = '';
        }
      },

      togglePicker() {
        if (this.isPickerOpen) {
          this.closePicker();
        }

        else {
          this.openPicker();
        }
      }
    }
  };
</script>
